/**
 * Turns a raw error (database / server / network text) into a message a user
 * understands, keeping the exact text for IT support. Used by the global error
 * toast, so every screen gets the same behaviour.
 */
export interface DescribedError {
  /** plain-language sentence for the user */
  friendly: string;
  /** what the user can do about it, if anything */
  hint?: string;
  /** true when IT should look at it (gets a reference and a server log entry) */
  forSupport: boolean;
}

/** column abbreviations used across the ERP tables */
const WORDS: Record<string, string> = {
  DEL: 'delivery', QTY: 'quantity', AMT: 'amount', CUST: 'customer', SMAN: 'salesman', DT: 'date',
  REF: 'reference', DESC: 'description', ADDR: 'address', PH: 'phone', NO: 'number', DISC: 'discount',
  PERC: 'percent', LOC: 'location', DIVN: 'division', COMP: 'company', TXN: 'transaction', UOM: 'unit',
  RMKS: 'remarks', REMARKS: 'remarks', ACNT: 'account', CURR: 'currency', EXP: 'expiry', STK: 'stock',
};
/** "SOH_DEL_TIME_SLOT" -> "delivery time slot" */
const fieldName = (column: string) =>
  column
    .replace(/^[A-Z]{2,6}_/, '')
    .split('_')
    .map((w) => WORDS[w] ?? w.toLowerCase())
    .join(' ');

const RULES: { test: RegExp; describe: (m: RegExpMatchArray) => Omit<DescribedError, 'forSupport'> }[] = [
  {
    test: /ORA-12899:.*?"([A-Z0-9_$#]+)"\s*\(actual:\s*(\d+),\s*maximum:\s*(\d+)\)/i,
    describe: (m) => ({
      friendly: `A value is longer than the system allows (${fieldName(m[1])}: ${m[2]} characters, limit ${m[3]}).`,
      hint: 'Shorten the value if you typed it; otherwise IT needs to fix the setup.',
    }),
  },
  { test: /ORA-12899/i, describe: () => ({ friendly: 'A value is longer than the system allows.' }) },
  {
    test: /ORA-00001/i,
    describe: () => ({ friendly: 'This record already exists, so it cannot be saved again.', hint: 'Check for a duplicate, then refresh.' }),
  },
  {
    test: /ORA-01400:.*?"([A-Z0-9_$#]+)"\)/i,
    describe: (m) => ({ friendly: `A required value is missing (${fieldName(m[1])}).` }),
  },
  {
    test: /ORA-02291/i,
    describe: () => ({ friendly: 'The record refers to master data that does not exist (for example a customer, item or code).' }),
  },
  {
    test: /ORA-02292/i,
    describe: () => ({ friendly: 'This record is used elsewhere and cannot be removed.' }),
  },
  { test: /ORA-01722/i, describe: () => ({ friendly: 'A number was expected but something else was entered.' }) },
  { test: /ORA-0(1830|1840|1841|1843|1847|1858|1861)/i, describe: () => ({ friendly: 'A date is not in a valid format.' }) },
  { test: /ORA-00054|ORA-00060/i, describe: () => ({ friendly: 'Someone else is changing the same record right now.', hint: 'Wait a moment and try again.' }) },
  { test: /ORA-01403/i, describe: () => ({ friendly: 'The data needed for this was not found.' }) },
  { test: /ORA-01476/i, describe: () => ({ friendly: 'A calculation divided by zero (check quantities and rates).' }) },
  { test: /ORA-20\d{3}:\s*(.+)/i, describe: (m) => ({ friendly: m[1].split(/\n|ORA-/)[0].trim() }) },
  { test: /ORA-(06550|04063|06508)|PLS-\d+/i, describe: () => ({ friendly: 'A database program is out of date or not installed.', hint: 'IT needs to deploy the latest database changes.' }) },
  // older API builds strip the ORA-/PLS- code and keep only "line 1, column 7: ... This error occurred in the procedure: X"
  {
    test: /line \d+, column \d+[\s\S]*This error occurred in the procedure:\s*([A-Za-z0-9_$#.]+)/i,
    describe: (m) => ({ friendly: 'A database program is out of date or not installed.', hint: `IT needs to deploy ${m[1]} to this company's database.` }),
  },
  { test: /NJS-040|pool.*timeout|queueTimeout/i, describe: () => ({ friendly: 'The server is busy.', hint: 'Try again in a minute.' }) },
  { test: /NJS-\d+/i, describe: () => ({ friendly: 'The server could not run this request.' }) },
  { test: /Network Error|Failed to fetch|ECONNREFUSED|ERR_NETWORK/i, describe: () => ({ friendly: 'The server could not be reached.', hint: 'Check the connection and try again.' }) },
  { test: /timeout of \d+ms exceeded|ETIMEDOUT/i, describe: () => ({ friendly: 'The server took too long to answer.', hint: 'Try again; if it keeps happening, send the details to IT.' }) },
  { test: /status code 5\d\d/i, describe: () => ({ friendly: 'The server ran into a problem.' }) },
  { test: /status code 404/i, describe: () => ({ friendly: 'This function is not available on the server yet.' }) },
];

/** text that is a technical error rather than a message written for users */
export const isTechnical = (text: string | null | undefined) =>
  Boolean(text && /ORA-\d|PLS-\d|NJS-\d|SQLERRM|status code \d{3}|Network Error|ECONN|ETIMEDOUT|timeout of|^Error:\s|This error occurred in the procedure/i.test(text));

export const describeError = (detail: string | null | undefined, shown?: string | null): DescribedError => {
  const text = String(detail || shown || '');
  for (const rule of RULES) {
    const m = text.match(rule.test);
    if (m) return { ...rule.describe(m), forSupport: true };
  }
  if (isTechnical(text)) {
    return { friendly: 'The system could not complete this action.', hint: 'Send the details to IT support.', forSupport: true };
  }
  return { friendly: text || 'Something went wrong.', forSupport: Boolean(detail) };
};

/** short reference the user can read out or paste: ERP-261022-142233-K7Q */
export const newErrorReference = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  const rand = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `ERP-${String(d.getFullYear()).slice(2)}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}-${rand}`;
};
